/**
 * The draft an editor builds, and its translation into Keystone's `editFile`.
 *
 * A draft is authored against the page rail — Keystone's `PAGE` renditions of
 * the head version's canonical — so a highlight's points are stored in that
 * image's own space (normalized, origin top-left) and only turned into the
 * post-rotation space Bindery draws in when the edit is sent.
 */

export type Rotation = 0 | 90 | 180 | 270;

export interface Point {
  x: number;
  y: number;
}

export const HIGHLIGHT_COLORS = [
  { name: 'Yellow', hex: '#ffe135' },
  { name: 'Pink', hex: '#ff5fb4' },
  { name: 'Teal', hex: '#14b8a6' },
] as const;

export type HighlightColor = (typeof HIGHLIGHT_COLORS)[number]['hex'];

/** A fraction of the page's height, like every size Keystone takes. */
export const HIGHLIGHT_WIDTH = 0.022;
export const HIGHLIGHT_OPACITY = 0.35;

/** Keystone and Bindery refuse a stroke wider than a quarter of the page. */
const MAX_STROKE_WIDTH = 0.25;

export interface HighlightStroke {
  color: HighlightColor;
  /** In the unrotated page image's space. */
  points: Point[];
}

export interface PageDraft {
  /** 1-based into the head version's canonical PDF. */
  sourcePage: number;
  rotate: Rotation;
  deleted: boolean;
  highlights: HighlightStroke[];
  /** The rendition's width / height, which a rotated stroke width needs. */
  aspect: number;
}

export interface PageSource {
  pageNo: number | null;
  width: number | null;
  height: number | null;
}

export function initialDrafts(pages: readonly PageSource[]): PageDraft[] {
  return pages
    .filter((page): page is PageSource & { pageNo: number } => page.pageNo !== null)
    .map((page) => ({
      sourcePage: page.pageNo,
      rotate: 0,
      deleted: false,
      highlights: [],
      aspect: page.width && page.height ? page.width / page.height : 1,
    }));
}

export function turn(rotate: Rotation, by: 90 | -90): Rotation {
  return ((((rotate + by) % 360) + 360) % 360) as Rotation;
}

/** A point in the unrotated image, as it lands once the page is turned clockwise. */
export function rotatePoint(point: Point, rotate: Rotation): Point {
  switch (rotate) {
    case 90:
      return { x: 1 - point.y, y: point.x };
    case 180:
      return { x: 1 - point.x, y: 1 - point.y };
    case 270:
      return { x: point.y, y: 1 - point.x };
    default:
      return point;
  }
}

/** The inverse of `rotatePoint`: where a point on the turned page sits in the image. */
export function unrotatePoint(point: Point, rotate: Rotation): Point {
  return rotatePoint(point, ((360 - rotate) % 360) as Rotation);
}

export function hasChanges(drafts: readonly PageDraft[]): boolean {
  return drafts.some((draft) => draft.deleted || draft.rotate !== 0 || draft.highlights.length);
}

export interface PointInput {
  x: number;
  y: number;
}

export interface AnnotationInput {
  kind: 'INK';
  opacity: number;
  strokes: PointInput[][];
  width: number;
  color: string;
}

export interface PageEditInput {
  sourcePage: number;
  rotate: Rotation;
  annotations: AnnotationInput[];
}

export interface FileEditInput {
  pages: PageEditInput[];
}

/**
 * The drafts as the page walk `editFile` takes.
 *
 * `pageCount` is the canonical's, and every page past the drafts — the rail is
 * windowed — rides through untouched, since omitting a page from the walk is
 * how Keystone reads a deletion.
 */
export function toEditInput(drafts: readonly PageDraft[], pageCount: number): FileEditInput {
  const bySource = new Map(drafts.map((draft) => [draft.sourcePage, draft]));
  const pages: PageEditInput[] = [];
  for (let sourcePage = 1; sourcePage <= pageCount; sourcePage += 1) {
    const draft = bySource.get(sourcePage);
    if (!draft) {
      pages.push({ sourcePage, rotate: 0, annotations: [] });
      continue;
    }
    if (draft.deleted) {
      continue;
    }
    pages.push({
      sourcePage,
      rotate: draft.rotate,
      annotations: draft.highlights.map((stroke) => highlightInput(stroke, draft)),
    });
  }
  return { pages };
}

function highlightInput(stroke: HighlightStroke, draft: PageDraft): AnnotationInput {
  // A quarter turn swaps the page's height for its width, and the width is a
  // fraction of whichever is now the height.
  const sideways = draft.rotate === 90 || draft.rotate === 270;
  const width = sideways ? HIGHLIGHT_WIDTH / draft.aspect : HIGHLIGHT_WIDTH;
  return {
    kind: 'INK',
    opacity: HIGHLIGHT_OPACITY,
    width: round(Math.min(width, MAX_STROKE_WIDTH)),
    color: stroke.color,
    strokes: [stroke.points.map((point) => roundPoint(rotatePoint(point, draft.rotate)))],
  };
}

export function clampPoint(point: Point): Point {
  return { x: Math.min(1, Math.max(0, point.x)), y: Math.min(1, Math.max(0, point.y)) };
}

function roundPoint(point: Point): PointInput {
  return { x: round(point.x), y: round(point.y) };
}

function round(value: number): number {
  return Math.round(value * 10_000) / 10_000;
}

/** A job that has reached no answer yet — an edit that may still land. */
export function isJobInFlight(job: { status: string | null } | null | undefined): boolean {
  return job?.status === 'PENDING' || job?.status === 'DISPATCHED';
}
